import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FileText, Users, BarChart3, CheckCircle, 
  ArrowRight, Shield, Zap, Globe, Clock, Lock, Layers,
  ClipboardList, BookOpen, Settings, AlertTriangle, 
  TrendingUp, FileCheck, Workflow, Brain, Search, 
  MessageSquare, Lightbulb
} from 'lucide-react';
import './Products.css';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Module {
  id: string;
  title: string;
  shortTitle: string;
  subtitle: string;
  icon: React.ReactNode;
  description: string;
  features: Feature[];
  benefits: string[];
  useCases?: string[];
}

const Products: React.FC = () => {
  const { productId } = useParams<{ productId?: string }>();

  const modules: Module[] = [
    {
      id: 'content-management',
      title: 'Content Management System',
      shortTitle: 'Content Management',
      subtitle: 'Complete Document Lifecycle Management for Regulated Environments',
      icon: <FileText size={48} />,
      description: 'A comprehensive content management system designed for regulated industries. Manage the complete document lifecycle from Draft → Review → Approval → Effective → Obsolete with full version control, access control, and regulatory alignment.',
      features: [
        { icon: <Layers size={24} />, title: 'Document Lifecycle Management', description: 'Complete lifecycle from Draft to Obsolete with controlled transitions' },
        { icon: <Clock size={24} />, title: 'Version Control', description: 'Maintain document history and compare versions to track evolution' },
        { icon: <Lock size={24} />, title: 'Access Control', description: 'Role-based access (Author, Reviewer, Approver) with confidentiality levels' },
        { icon: <FileText size={24} />, title: 'Linked Content', description: 'Link Forms, Annexures to parent documents for easy navigation' },
        { icon: <Users size={24} />, title: 'Real-Time Collaboration', description: 'Multiple users can review/comment simultaneously with controlled commenting' },
        { icon: <Shield size={24} />, title: 'Audit Trail', description: 'Track every action with exportable logs for regulatory inspections' },
      ],
      benefits: [
        '21 CFR Part 11 compliant e-signatures with multi-step approval workflows',
        'Pre-configured templates for GMP, GLP, GCP compliance',
        'Document Distribution and Reconciliation tracking',
        'Controlled Issuance, tracking, confirmation, and verification',
        'Configurable Dashboards and Reports per user'
      ],
      useCases: [
        'SOPs and Work Instructions',
        'Controlled Document Management',
        'Regulatory Submission Documents',
        'Master Batch Records',
        'Document Distribution and Reconciliation'
      ]
    },
    {
      id: 'training-management',
      title: 'Training Management System',
      shortTitle: 'Training Management',
      subtitle: 'Competency-First Training Mapped to Roles with Automated Learning Triggers',
      icon: <Users size={48} />,
      description: 'Ensure your team is always trained and certified with our competency-based training management system. Automatically assign training based on job role, department, and responsibilities with auto-triggered learning when documents change.',
      features: [
        { icon: <Users size={24} />, title: 'Role-Based Training Assignment', description: 'Automatically assign training based on job role, department, and responsibilities' },
        { icon: <Clock size={24} />, title: 'Training Lifecycle Management', description: 'Status tracking with due dates and escalation workflows' },
        { icon: <Shield size={24} />, title: 'Electronic Signatures & Compliance', description: '21 CFR Part 11 compliant e-signatures for training completion' },
        { icon: <BookOpen size={24} />, title: 'Interactive Content', description: 'Videos, quizzes, and simulations for better engagement' },
        { icon: <BarChart3 size={24} />, title: 'Compliance Dashboard', description: 'KPIs: Completion rates, overdue training, upcoming deadlines' },
        { icon: <Zap size={24} />, title: 'Automated Triggers', description: 'Auto-triggered learning when documents change with adaptive assessments' },
      ],
      benefits: [
        'Link training to SOPs and controlled documents automatically',
        'Manage complete training journey from assignment to verification',
        'Audit-ready logs for regulatory inspections',
        'Attach forms and templates for practical exercises',
        'Drill-down reports by user, department, or document'
      ],
      useCases: [
        'New Hire Onboarding',
        'SOP Training & Read Acknowledgment',
        'Annual Refresher Training',
        'Certification Tracking',
        'Competency Gap Analysis'
      ]
    },
    {
      id: 'quality-management',
      title: 'Quality Management System',
      shortTitle: 'Quality Management',
      subtitle: 'Comprehensive Quality Management Solution',
      icon: <BarChart3 size={48} />,
      description: 'The core of QC-Metric - a comprehensive quality management system with deviation management, CAPA workflows, change control, audit management, and risk assessment. Pre-configured workflows for GMP, ISO 9001, ICH Q10 compliance.',
      features: [
        { icon: <AlertTriangle size={24} />, title: 'Deviation Management', description: 'Log, track, and resolve deviations linked to impacted SOPs, batches, or processes' },
        { icon: <CheckCircle size={24} />, title: 'CAPA Workflows', description: 'Initiate CAPAs from deviations or audit findings with root cause analysis' },
        { icon: <ClipboardList size={24} />, title: 'Change Control', description: 'Manage controlled changes to processes, equipment, or documents' },
        { icon: <Shield size={24} />, title: 'Audit & Inspection Management', description: 'Schedule internal/external audits and track findings' },
        { icon: <BarChart3 size={24} />, title: 'Risk Assessment', description: 'Integrated risk scoring for deviations and changes' },
        { icon: <FileCheck size={24} />, title: 'Validation Lifecycle', description: 'Create, review, approve, execute and summarize validation documentation' },
      ],
      benefits: [
        'Pre-configured workflows for GMP, ISO 9001, ICH Q10',
        'Validation-ready system with audit trails',
        '21 CFR Part 11 compliant approvals for QMS records',
        'Prioritize actions based on criticality',
        'Complete investigation and closure workflows'
      ],
      useCases: [
        'CAPA (Corrective & Preventive Actions)',
        'Deviation & OOS/OOT Investigations',
        'Change Control Management',
        'Complaints Handling',
        'Supplier Quality Management',
        'Audit Management',
        'Risk Assessment'
      ]
    },
    {
      id: 'workflow-forms',
      title: 'Workflow Based Forms',
      shortTitle: 'Workflow Forms',
      subtitle: 'Dynamic Workflow Engine with Form Builder for Customization',
      icon: <Workflow size={48} />,
      description: 'Create and manage digital forms with our dynamic workflow engine. Drag-and-drop form designer to create custom fields, validations, and layouts. Easily configure workflows without coding for SOP management, training, audits, and quality events.',
      features: [
        { icon: <Settings size={24} />, title: 'Dynamic Workflow Engine', description: 'Easily configure workflows without coding' },
        { icon: <Workflow size={24} />, title: 'Form Builder', description: 'Drag-and-drop form designer with custom fields and validations' },
        { icon: <Globe size={24} />, title: 'Domain-Agnostic Flexibility', description: 'Supports pharma, biotech, MedTech with configurable templates' },
        { icon: <FileCheck size={24} />, title: 'Electronic Signatures', description: '21 CFR Part 11, EU Annex 11 compliant e-signatures' },
        { icon: <Zap size={24} />, title: 'Integration Ready', description: 'Connect with ERP, LIMS, CRM, and regulatory portals via API' },
        { icon: <Lock size={24} />, title: 'Regulatory Compliance', description: 'GDPR, ISO 9001 ready with built-in audit trails' },
      ],
      benefits: [
        'Adapt processes for SOP management, training, audits, and quality events',
        'Create custom fields, validations, and layouts per client requirements',
        'API-first approach for seamless interoperability',
        'Configurable templates for GMP, GLP, GCP compliance',
        'Built-in audit trails and e-signatures'
      ],
      useCases: [
        'Equipment Logs & Checklists',
        'Batch Records',
        'Safety Inspection Forms',
        'Environmental Monitoring',
        'Calibration Records',
        'Custom Quality Forms'
      ]
    }
  ];

  // Find the current module if viewing a module detail page
  const currentModule = productId && productId !== 'qc-metric' 
    ? modules.find(m => m.id === productId) 
    : null;

  // QC-Metric Overview Page
  if (productId === 'qc-metric' || !productId) {
    return (
      <div className="products-page">
        <section className="products-hero">
          <div className="container">
            <div className="products-hero-content">
              <span className="badge">Aurexa Product</span>
              <h1>QC-Metric</h1>
              <p className="hero-subtitle">
                The Complete Quality Management Platform for Regulated Industries
              </p>
              <p>
                QC-Metric is Aurexa's flagship quality management solution - a unified platform 
                that brings together Content Management, Training Management, Quality Management, 
                and Workflow-based Forms into one seamless, compliant system.
              </p>
              <div className="hero-ctas">
                <Link to="/demo" className="btn btn-primary">
                  Book a Demo <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="btn btn-outline-white">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section-header">
              <span className="badge">Platform Modules</span>
              <h2>Four Integrated Modules, One Unified Platform</h2>
              <p>Each module works seamlessly together while also functioning independently.</p>
            </div>
            <div className="products-grid">
              {modules.map((module) => (
                <Link key={module.id} to={`/products/${module.id}`} className="product-card">
                  <div className="product-card-icon">{module.icon}</div>
                  <h3>{module.shortTitle}</h3>
                  <p>{module.subtitle}</p>
                  <span className="product-link">
                    Learn More <ArrowRight size={16} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-alt">
          <div className="container">
            <div className="section-header">
              <span className="badge">Why QC-Metric</span>
              <h2>Built for Regulated Industries</h2>
            </div>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon"><Shield size={32} /></div>
                <h3>Pre-Validated</h3>
                <p>Comes with validation templates and documentation for faster deployment</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon"><Zap size={32} /></div>
                <h3>Drag-and-Configure</h3>
                <p>Low consulting dependency with visual workflow and form builders</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon"><Globe size={32} /></div>
                <h3>Native Integrations</h3>
                <p>Connect with ERP, LIMS, CRM, and regulatory portals via API-first approach</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon"><BarChart3 size={32} /></div>
                <h3>Real-time Analytics</h3>
                <p>Live KPIs and predictive quality flags for proactive management</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon"><Lock size={32} /></div>
                <h3>Regulatory Compliance</h3>
                <p>21 CFR Part 11, EU Annex 11, GDPR, ISO 9001 ready with audit trails</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon"><Users size={32} /></div>
                <h3>Modern Role-Based UI</h3>
                <p>Guided tasks for faster onboarding and intuitive experience</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section ai-section">
          <div className="container">
            <div className="section-header">
              <span className="badge">AI/ML Intelligence</span>
              <h2>Advanced AI Capabilities to Transform Quality Operations</h2>
              <p>Leverage artificial intelligence and machine learning to automate compliance and predict quality issues.</p>
            </div>
            <div className="ai-features-grid">
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><Search size={28} /></div>
                <h3>Smart Search & Semantic Retrieval</h3>
                <p>AI-powered semantic search finds related SOPs, annexures, and forms beyond keywords.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><Shield size={28} /></div>
                <h3>Automated Compliance Checks</h3>
                <p>AI models scan documents for missing regulatory clauses and outdated references.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><FileText size={28} /></div>
                <h3>Version Comparison & Change Impact</h3>
                <p>ML highlights critical changes between document versions with automated impact analysis.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><BookOpen size={28} /></div>
                <h3>Content Summarization</h3>
                <p>Generate concise summaries of lengthy regulatory guidelines or SOPs for quick review.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><Brain size={28} /></div>
                <h3>Adaptive Learning Paths</h3>
                <p>AI recommends training based on role and compliance gaps with skill gap analysis.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><TrendingUp size={28} /></div>
                <h3>Predictive Analytics</h3>
                <p>ML predicts training compliance, identifies skill gaps, and recommends preventive actions.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><AlertTriangle size={28} /></div>
                <h3>Deviation & CAPA Prediction</h3>
                <p>ML models predict potential deviations from historical data before issues occur.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><MessageSquare size={28} /></div>
                <h3>Natural Language Chatbots</h3>
                <p>Virtual assistants answer SOP-related queries during training, reducing dependency on trainers.</p>
              </div>
              <div className="ai-feature-card">
                <div className="ai-feature-icon"><Lightbulb size={28} /></div>
                <h3>Auto-Generated Questionnaires</h3>
                <p>Based on SOP content, AI auto-generates questions (Descriptive, MCQs) for assessments.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="product-cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Transform Your Quality Management?</h2>
              <p>See how QC-Metric can streamline your compliance processes.</p>
              <Link to="/demo" className="btn btn-primary">
                Book a Demo <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Module not found
  if (!currentModule) {
    return (
      <div className="products-page">
        <section className="products-hero">
          <div className="container">
            <h1>Module Not Found</h1>
            <Link to="/products/qc-metric" className="btn btn-primary">
              View QC-Metric Overview
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Module detail view
  return (
    <div className="products-page">
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-content">
            <div className="product-icon">{currentModule.icon}</div>
            <div className="product-info">
              <span className="badge">QC-Metric Module</span>
              <h1>{currentModule.title}</h1>
              <p className="product-subtitle">{currentModule.subtitle}</p>
              <p className="product-description">{currentModule.description}</p>
              <div className="product-ctas">
                <Link to="/demo" className="btn btn-primary">
                  Request Demo <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="btn btn-outline-white">
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="badge">Key Features</span>
            <h2>Capabilities</h2>
          </div>
          <div className="features-grid">
            {currentModule.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {currentModule.useCases && (
        <section className="section section-alt">
          <div className="container">
            <div className="section-header">
              <span className="badge">Use Cases</span>
              <h2>Common Applications</h2>
            </div>
            <div className="modules-grid">
              {currentModule.useCases.map((useCase, index) => (
                <div key={index} className="module-card">
                  <CheckCircle size={20} />
                  <span>{useCase}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={`section ${currentModule.useCases ? '' : 'section-alt'}`}>
        <div className="container">
          <div className="benefits-section">
            <div className="section-header">
              <span className="badge">Benefits</span>
              <h2>Why Choose {currentModule.shortTitle}</h2>
            </div>
            <div className="benefits-list">
              {currentModule.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <CheckCircle size={24} />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="other-modules-section">
        <div className="container">
          <div className="section-header">
            <span className="badge">Explore More</span>
            <h2>Other QC-Metric Modules</h2>
          </div>
          <div className="other-modules-grid">
            {modules.filter(m => m.id !== currentModule.id).map((module) => (
              <Link key={module.id} to={`/products/${module.id}`} className="other-module-card">
                <div className="other-module-icon">{module.icon}</div>
                <h3>{module.shortTitle}</h3>
                <ArrowRight size={16} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="product-cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>See how {currentModule.shortTitle} can transform your quality management.</p>
            <Link to="/demo" className="btn btn-primary">
              Book a Demo <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
